import * as React from "react";
import { useApolloClient, useQuery } from "@apollo/client";
import { toast } from "react-toastify";
import Button from "react-bootstrap/Button";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import Card from "react-bootstrap/Card";
import Spinner from "react-bootstrap/Spinner";
import Table from "react-bootstrap/Table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleLeft,
  faAngleRight,
  faAnglesLeft,
  faAnglesRight,
  faCopy,
} from "@fortawesome/free-solid-svg-icons";
import { gettext } from "i18n";
import { getChartSeriesColor } from "utils/datacharts";
import {
  DischargePeriod,
  formatHourRange,
  formatLocalDay,
  formatPeriodKey,
  getBrowserTimeZone,
  getDayWindows,
  getHourStarts,
  sumAccruedValues,
  sumHourlyValues,
} from "utils/discharge";
import { formatNumberLocale, formatValueForTable } from "utils/formatting";
import { getLiteralArray } from "utils/hasura";
import {
  GET_ACCRUED_FORMULA_VALUES,
  GET_ACCRUED_HOURLY_VALUES,
  GET_DISCHARGE_TABLE_SOURCES,
  getSourceUnitSymbol,
} from "utils/stations";
import {
  GetAccruedFormulaValues,
  GetAccruedFormulaValuesVariables,
} from "utils/__generated__/GetAccruedFormulaValues";
import {
  GetAccruedHourlyValues,
  GetAccruedHourlyValuesVariables,
} from "utils/__generated__/GetAccruedHourlyValues";
import {
  GetDischargeTableSources,
  GetDischargeTableSourcesVariables,
} from "utils/__generated__/GetDischargeTableSources";

const DEFAULT_TITLES: Record<DischargePeriod, string> = {
  hourly: "Total Hourly Discharge",
  daily: "Total Daily Discharge",
  monthly: "Total Monthly Discharge",
  annual: "Total Annual Discharge",
};

/**
 * Copies a table to the clipboard. The contents may still be loading: Safari
 * only allows writing during the click, so the write starts right away with
 * a promise of the text where the browser supports that.
 */
export const copyTableToClipboard = async (
  tableContents: string | Promise<string>,
) => {
  try {
    try {
      if (typeof ClipboardItem === "undefined") {
        throw new Error("ClipboardItem not supported");
      }
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/plain": Promise.resolve(tableContents).then(
            (text) => new Blob([text], { type: "text/plain" }),
          ),
        }),
      ]);
    } catch (err) {
      // Browsers without ClipboardItem promises
      await navigator.clipboard.writeText(await tableContents);
    }
    toast.success(gettext("Value copied to clipboard"));
  } catch (err) {
    console.error("Failed to copy to clipboard: ", err);
    toast.error(gettext("Action failed"));
  }
};

interface DischargeTableCardProps {
  period: DischargePeriod;
  title?: string;
  displayUnit?: string;
  sourceUUIDs: string[];
  children: React.ReactNode;
}

/**
 * Card with the table title and the names of its data sources. Only the
 * source metadata is loaded for the names, not their datapoints.
 */
const DischargeTableCard: React.FC<DischargeTableCardProps> = ({
  period,
  title,
  displayUnit,
  sourceUUIDs,
  children,
}) => {
  const { data } = useQuery<
    GetDischargeTableSources,
    GetDischargeTableSourcesVariables
  >(GET_DISCHARGE_TABLE_SOURCES, {
    variables: { uuids: getLiteralArray(sourceUUIDs ?? []) },
    skip: !sourceUUIDs?.length,
  });
  // Keep the order of the widget's sources so that colours stay stable
  const sources = (sourceUUIDs ?? []).flatMap((sourceUUID) => {
    const source = data?.source.find((s) => s.source_uuid === sourceUUID);
    return source ? [source] : [];
  });

  return (
    <Card className="DataSourceCard">
      <Card.Header>
        <h5>
          {gettext(title || DEFAULT_TITLES[period])} [{displayUnit ?? "l"}]
        </h5>
        {!!sources.length && (
          <span className="additional-data-sources">
            {sources.map((source, idx) => (
              <h6
                key={`additional_data_source_${source.source_uuid}`}
                className="additional-data-source"
                style={{ color: getChartSeriesColor(idx) }}
              >
                {`${source.station?.name} (${source.station?.identifier}) - ${
                  source.name
                } ${getSourceUnitSymbol(source)}`}
              </h6>
            ))}
          </span>
        )}
      </Card.Header>
      <Card.Body>{children}</Card.Body>
    </Card>
  );
};

const CopyButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <span className="copy-to-clipboard standalone" onClick={onClick}>
    <FontAwesomeIcon icon={faCopy} />
    <span>copy</span>
  </span>
);

interface TotalDischargeTableProps {
  startDate: Date;
  endDate: Date;
  sourceUUIDs: string[];
  title?: string;
  period: Exclude<DischargePeriod, "hourly">;
  displayUnit?: string;
}

/**
 * Total discharge per day, month or year
 */
export const TotalDischargeTable: React.FC<TotalDischargeTableProps> = ({
  startDate,
  endDate,
  sourceUUIDs,
  title,
  displayUnit,
  period,
}) => {
  const { data, loading } = useQuery<
    GetAccruedFormulaValues,
    GetAccruedFormulaValuesVariables
  >(GET_ACCRUED_FORMULA_VALUES, {
    variables: {
      uuids: getLiteralArray(sourceUUIDs ?? []),
      startDate,
      endDate,
      timeZone: getBrowserTimeZone(),
    },
    skip: !sourceUUIDs?.length,
  });
  const sums = React.useMemo(
    () =>
      sumAccruedValues(data?.hasura_accrued_values ?? [], period, displayUnit),
    [data, period, displayUnit],
  );
  const sortedKeys = Object.keys(sums).sort();
  const periodHeader =
    period === "annual"
      ? gettext("Year")
      : period === "monthly"
        ? gettext("Month")
        : gettext("Date");

  const copyToClipboard = () => {
    let tableContents = `${periodHeader}\tSum\n`;
    sortedKeys.forEach((key) => {
      tableContents += `${formatPeriodKey(key, period)}\t${formatValueForTable(
        Math.round(sums[key]),
      )}\n`;
    });
    copyTableToClipboard(tableContents);
  };

  return (
    <DischargeTableCard
      period={period}
      title={title}
      displayUnit={displayUnit}
      sourceUUIDs={sourceUUIDs}
    >
      {loading && <Spinner animation="border" />}
      {!loading && (
        <>
          {!!sortedKeys.length && <CopyButton onClick={copyToClipboard} />}
          <Table>
            <thead>
              <tr>
                <th>{periodHeader}</th>
                <th>Sum</th>
              </tr>
            </thead>
            <tbody>
              {sortedKeys.map((key) => (
                <tr key={key}>
                  <td>{formatPeriodKey(key, period)}</td>
                  <td>{formatNumberLocale(sums[key], 0)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      )}
    </DischargeTableCard>
  );
};

interface HourlyDischargeTableProps {
  startDate: Date;
  endDate: Date;
  sourceUUIDs: string[];
  title?: string;
  displayUnit?: string;
}

/**
 * Total discharge per hour, one local calendar day per page. Only the shown
 * day is queried, so long date ranges stay fast.
 */
export const HourlyDischargeTable: React.FC<HourlyDischargeTableProps> = ({
  startDate,
  endDate,
  sourceUUIDs,
  title,
  displayUnit,
}) => {
  const client = useApolloClient();
  const startTime = startDate.getTime();
  const endTime = endDate.getTime();
  const dayWindows = React.useMemo(
    () => getDayWindows(new Date(startTime), new Date(endTime)),
    [startTime, endTime],
  );
  // Show the most recent day first, also after the date range changes
  const [pageIndex, setPageIndex] = React.useState(dayWindows.length - 1);
  React.useEffect(() => {
    setPageIndex(dayWindows.length - 1);
  }, [dayWindows]);
  const lastPage = dayWindows.length - 1;
  const page = Math.min(Math.max(pageIndex, 0), lastPage);
  const dayWindow = dayWindows[page];
  const uuids = getLiteralArray(sourceUUIDs ?? []);
  const timeZone = getBrowserTimeZone();
  // Hourly values in m³ are often below one, so show some decimals
  const decimals = displayUnit === "m³" ? 2 : 0;

  const { data, loading } = useQuery<
    GetAccruedHourlyValues,
    GetAccruedHourlyValuesVariables
  >(GET_ACCRUED_HOURLY_VALUES, {
    variables: {
      uuids,
      startDate: dayWindow?.start,
      endDate: dayWindow?.end,
      timeZone,
    },
    skip: !sourceUUIDs?.length || !dayWindow,
  });
  const sums = React.useMemo(
    () =>
      sumHourlyValues(data?.hasura_accrued_hourly_values ?? [], displayUnit),
    [data, displayUnit],
  );
  const hourStarts = React.useMemo(
    () => (dayWindow ? getHourStarts(dayWindow) : []),
    [dayWindow],
  );

  // Copies the whole date range, not just the shown day
  const copyToClipboard = () =>
    copyTableToClipboard(
      client
        .query<GetAccruedHourlyValues, GetAccruedHourlyValuesVariables>({
          query: GET_ACCRUED_HOURLY_VALUES,
          variables: { uuids, startDate, endDate, timeZone },
        })
        .then((result) => {
          const allSums = sumHourlyValues(
            result.data.hasura_accrued_hourly_values,
            displayUnit,
          );
          let tableContents = `${gettext("Hour")}\tSum\n`;
          Array.from(allSums.keys())
            .sort((a, b) => a - b)
            .forEach((time) => {
              const hourStart = new Date(time);
              tableContents += `${formatLocalDay(hourStart)} ${formatHourRange(
                hourStart,
              )}\t${formatValueForTable(allSums.get(time), decimals)}\n`;
            });
          return tableContents;
        }),
    );

  const pageButton = (
    target: number,
    icon: typeof faAngleLeft,
    label: string,
  ) => (
    <Button
      variant="outline-primary"
      size="sm"
      disabled={target === page || target < 0 || target > lastPage}
      onClick={() => setPageIndex(target)}
      title={label}
      aria-label={label}
    >
      <FontAwesomeIcon icon={icon} />
    </Button>
  );

  return (
    <DischargeTableCard
      period="hourly"
      title={title}
      displayUnit={displayUnit}
      sourceUUIDs={sourceUUIDs}
    >
      {!!dayWindow && (
        <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
          <ButtonGroup>
            {pageButton(0, faAnglesLeft, gettext("First day"))}
            {pageButton(page - 1, faAngleLeft, gettext("Previous day"))}
          </ButtonGroup>
          <span className="hourly-discharge-day">
            <b>{formatLocalDay(dayWindow.start)}</b>{" "}
            {`(${page + 1}/${dayWindows.length})`}
          </span>
          <ButtonGroup>
            {pageButton(page + 1, faAngleRight, gettext("Next day"))}
            {pageButton(lastPage, faAnglesRight, gettext("Last day"))}
          </ButtonGroup>
          {loading && <Spinner animation="border" size="sm" />}
          <span className="ms-auto">
            <CopyButton onClick={copyToClipboard} />
          </span>
        </div>
      )}
      <Table>
        <thead>
          <tr>
            <th>{gettext("Hour")}</th>
            <th>Sum</th>
          </tr>
        </thead>
        <tbody>
          {hourStarts.map((hourStart) => {
            const sum = sums.get(hourStart.getTime());
            return (
              <tr key={hourStart.getTime()}>
                <td>{formatHourRange(hourStart)}</td>
                <td>
                  {loading || sum === undefined
                    ? "–"
                    : formatNumberLocale(sum, decimals)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </DischargeTableCard>
  );
};
